import * as React from 'react';

import { SaveFooterOptions } from 'src/components/resourceDialog/DialogFooter';
import {
  BaseDialogContextType,
  createBaseDialogContextDefaults,
  useBaseDialogContextValue,
} from 'src/containers/shared/useBaseDialogContext';
import {
  ProjectVirtualGraph,
  ProjectVirtualGraphOptions,
} from 'src/store/state/projects';
import * as copy from 'src/templates/copy';

export type FooterOptions = SaveFooterOptions;

export type DataSourceUpdateParams = {
  dataSourceId: string;
  dataSourceType: string;
  dataSourceEndpoint?: string;
  connectionId: string | null;
};

export type ProjectVirtualGraphPreviewDialogContextType =
  BaseDialogContextType<FooterOptions> & {
    // Virtual graph specific state
    localVirtualGraph: ProjectVirtualGraph | null;
    setLocalVirtualGraph: React.Dispatch<
      React.SetStateAction<ProjectVirtualGraph | null>
    >;
    localVirtualGraphOptions: ProjectVirtualGraphOptions | null;
    setLocalVirtualGraphOptions: React.Dispatch<
      React.SetStateAction<ProjectVirtualGraphOptions | null>
    >;
    errorText: string;
    setErrorText: React.Dispatch<React.SetStateAction<string>>;
    resetLocalState: () => void;
    // Flag to track if data sources have been refreshed in this dialog session
    hasRefreshedDataSources: boolean;
    setHasRefreshedDataSources: React.Dispatch<React.SetStateAction<boolean>>;
    // Helper to update the virtual graph's data source
    updateVirtualGraphDataSource: (params: DataSourceUpdateParams) => void;
  };

export const ProjectVirtualGraphPreviewDialogContext =
  React.createContext<ProjectVirtualGraphPreviewDialogContextType>({
    ...createBaseDialogContextDefaults<FooterOptions>(),
    localVirtualGraph: null,
    setLocalVirtualGraph: () => {},
    localVirtualGraphOptions: null,
    setLocalVirtualGraphOptions: () => {},
    errorText: '',
    setErrorText: () => {},
    resetLocalState: () => {},
    hasRefreshedDataSources: false,
    setHasRefreshedDataSources: () => {},
    updateVirtualGraphDataSource: () => {},
  });

export const useProjectVirtualGraphPreviewDialog = () => {
  return React.useContext(ProjectVirtualGraphPreviewDialogContext);
};

export const useProjectVirtualGraphPreviewDialogContextValue = () => {
  const {
    fullSize,
    setFullSize,
    dialogTitle,
    setDialogTitle,
    headerBackButton,
    setHeaderBackButton,
    isLoading,
    setIsLoading,
    footerOptions,
    setFooterOptions,
  } = useBaseDialogContextValue<FooterOptions>(
    copy.components.virtualGraphPreviewDialog.title
  );

  const [localVirtualGraph, setLocalVirtualGraph] =
    React.useState<ProjectVirtualGraph | null>(null);
  const [localVirtualGraphOptions, setLocalVirtualGraphOptions] =
    React.useState<ProjectVirtualGraphOptions | null>(null);
  const [errorText, setErrorText] = React.useState('');
  const [hasRefreshedDataSources, setHasRefreshedDataSources] =
    React.useState(false);

  const resetLocalState = React.useCallback(() => {
    setFullSize(false);
    setDialogTitle(copy.components.virtualGraphPreviewDialog.title);
    setHeaderBackButton(null);
    setIsLoading(false);
    setFooterOptions({});
    setLocalVirtualGraph(null);
    setLocalVirtualGraphOptions(null);
    setErrorText('');
    setHasRefreshedDataSources(false);
  }, [
    setDialogTitle,
    setFooterOptions,
    setFullSize,
    setHeaderBackButton,
    setIsLoading,
  ]);

  const updateVirtualGraphDataSource = React.useCallback(
    (params: DataSourceUpdateParams) => {
      setLocalVirtualGraph((prev) =>
        prev
          ? {
              ...prev,
              connectionId: params.connectionId,
              dataSourceEndpoint: params.dataSourceEndpoint,
              dataSourceId: params.dataSourceId,
              dataSourceType: params.dataSourceType,
              from: {
                name: '',
                text: '',
                value: '',
              },
              fields: [],
              previewRows: [],
              dateLastUpdated: Date.now(),
            }
          : prev
      );
    },
    []
  );

  return React.useMemo(
    () => ({
      fullSize,
      setFullSize,
      dialogTitle,
      setDialogTitle,
      headerBackButton,
      setHeaderBackButton,
      isLoading,
      setIsLoading,
      footerOptions,
      setFooterOptions,
      localVirtualGraph,
      setLocalVirtualGraph,
      localVirtualGraphOptions,
      setLocalVirtualGraphOptions,
      errorText,
      setErrorText,
      resetLocalState,
      hasRefreshedDataSources,
      setHasRefreshedDataSources,
      updateVirtualGraphDataSource,
    }),
    [
      fullSize,
      dialogTitle,
      headerBackButton,
      isLoading,
      footerOptions,
      localVirtualGraph,
      localVirtualGraphOptions,
      errorText,
      resetLocalState,
      hasRefreshedDataSources,
      setHasRefreshedDataSources,
      setErrorText,
      setLocalVirtualGraph,
      setLocalVirtualGraphOptions,
      setFooterOptions,
      setFullSize,
      setDialogTitle,
      setHeaderBackButton,
      setIsLoading,
      updateVirtualGraphDataSource,
    ]
  );
};
